import {
    createContext,
    useContext,
    useState,
    ReactNode,
    useCallback,
} from "react";
import {
    PurchaseSummaryData,
    PurchaseReportData,
} from "@snackpass/snackpass-types";
import type { Moment } from "moment-timezone";
import { PaginationState, SortingState } from "@tanstack/react-table";

import {
    Channel,
    Fulfillment,
    PaymentMethod,
    Provider,
} from "#sales-report/types";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { selectCustomHourOffset, setCustomHourOffset } from "src/redux/slices/ordersSettings";

import { useQueryLabor } from "./hooks/use-query-labor";
import { usePurchaseReport } from "./use-query-mongo-orders";
import {
    useOrdersSearchParams,
    DefinedDates,
    getDefinedDatesTranslations,
} from "./use-orders-search-params";

export { DefinedDates, getDefinedDatesTranslations };

type LaborData = {
    totalLaborCost: number;
    laborCostPercentage: number;
    salesPerLaborHour: number;
    totalLaborHours: number;
};

type OrdersContextType = {
    // Table state
    pagination: PaginationState;
    setPagination: (
        updater:
            | PaginationState
            | ((prev: PaginationState) => PaginationState),
    ) => void;
    sorting: SortingState;
    setSorting: (
        updater: SortingState | ((prev: SortingState) => SortingState),
    ) => void;
    globalFilter: string;
    setGlobalFilter: (filter: string) => void;
    // Filters
    channels: Channel[];
    setChannels: (sources: Channel[]) => void;
    providers: Provider[];
    setProviders: (providers: Provider[]) => void;
    paymentMethods: PaymentMethod[];
    setPaymentMethods: (types: PaymentMethod[]) => void;
    startDate: Moment;
    endDate: Moment;
    duration: DefinedDates;
    customHourOffset: string;
    setCustomHourOffset: (offset: string) => void;
    setTimeRange: (range: { startDate: Moment; endDate: Moment }) => void;
    setDuration: (duration: DefinedDates) => void;
    fulfillments: Fulfillment[];
    setFulfillments: (types: Fulfillment[]) => void;
    employees: string[];
    setEmployees: (employees: string[]) => void;
    purchaseReportRows: {
        data?: PurchaseReportData[];
        isLoading: boolean;
    };
    purchaseReportTotals: {
        days?: PurchaseSummaryData[];
        weeks?: PurchaseSummaryData[];
        months?: PurchaseSummaryData[];
        payouts?: PurchaseSummaryData[];
        total?: PurchaseSummaryData;
        isLoading: boolean;
    };
    refunded: boolean;
    setRefunded: (show: boolean) => void;
    laborData: {
        data: LaborData;
        isLoading: boolean;
    };
    showEndOfDayReport: boolean;
    setShowEndOfDayReport: (show: boolean) => void;
};

const OrdersContext = createContext<OrdersContextType | undefined>(undefined);

type OrdersProviderProps = {
    children: ReactNode;
    transactionSources?: Channel[];
    /** Skip labor data for report consumers that never render it. */
    includeLabor?: boolean;
    /** Prevent non-date Orders URL filters from changing report data. */
    ignoreSearchFilters?: boolean;
};

export function OrdersProvider({
    children,
    transactionSources,
    includeLabor = true,
    ignoreSearchFilters = false,
}: OrdersProviderProps) {
    const {
        pagination,
        setPagination,
        sorting,
        setSorting,
        startDate,
        endDate,
        duration,
        setDuration,
        setTimeRange,
        channels,
        setChannels,
        providers,
        setProviders,
        fulfillments,
        setFulfillments,
        paymentMethods,
        setPaymentMethods,
        refunded,
        setRefunded,
        globalFilter,
        setGlobalFilter,
    } = useOrdersSearchParams();

    const [employees, setEmployees] = useState<string[]>([]);
    const [showEndOfDayReport, setShowEndOfDayReport] = useState(false);

    const dispatch = useAppDispatch();
    const customHourOffset = useAppSelector(selectCustomHourOffset) || "0";

    const handleSetCustomHourOffset = useCallback((offset: string) => {
        dispatch(setCustomHourOffset(offset));
    }, [dispatch]);

    const filters = {
        channels: transactionSources ?? (ignoreSearchFilters ? [] : channels),
        providers: ignoreSearchFilters ? [] : providers,
        paymentMethods: ignoreSearchFilters ? [] : paymentMethods,
        fulfillments: ignoreSearchFilters ? [] : fulfillments,
        employees,
        ...(!ignoreSearchFilters && refunded && { refunded }),
        customHourOffset,
    };

    const { data: rowsData, isLoading: rowsLoading } = usePurchaseReport(
        startDate,
        endDate,
        {
            ...filters,
            onlyRows: true,
            includePurchase: true,
            customHourOffset,
        },
    );

    const { data: totalsData, isLoading: totalsLoading } = usePurchaseReport(
        startDate,
        endDate,
        {
            ...filters,
            noRows: true,
            customHourOffset,
        },
    );

    const {
        data: laborData = {
            totalLaborCost: 0,
            laborCostPercentage: 0,
            salesPerLaborHour: 0,
            totalLaborHours: 0,
        },
        isLoading: isLaborLoading,
    } = useQueryLabor(startDate, endDate, includeLabor);

    return (
        <OrdersContext.Provider
            value={{
                pagination,
                setPagination,
                sorting,
                setSorting,
                globalFilter,
                setGlobalFilter,
                channels,
                setChannels,
                providers,
                setProviders,
                paymentMethods,
                setPaymentMethods,
                startDate,
                endDate,
                duration,
                customHourOffset,
                setCustomHourOffset: handleSetCustomHourOffset,
                setTimeRange,
                setDuration,
                fulfillments,
                setFulfillments,
                employees,
                setEmployees,
                purchaseReportRows: {
                    data: rowsData?.rows,
                    isLoading: rowsLoading,
                },
                purchaseReportTotals: {
                    days: totalsData?.days,
                    weeks: totalsData?.weeks,
                    months: totalsData?.months,
                    payouts: totalsData?.payouts,
                    total: totalsData?.total,
                    isLoading: totalsLoading,
                },
                refunded,
                setRefunded,
                laborData: {
                    data: laborData,
                    isLoading: isLaborLoading,
                },
                showEndOfDayReport,
                setShowEndOfDayReport,
            }}
        >
            {children}
        </OrdersContext.Provider>
    );
}

export function useOrders() {
    const context = useContext(OrdersContext);
    if (context === undefined) {
        throw new Error("useOrders must be used within an OrdersProvider");
    }
    return context;
}
